import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { datadogLogs } from '@datadog/browser-logs';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import Api from '@silbo/core';
import { mobileTheme } from '@silbo/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setZXingModuleOverrides } from '@yudiel/react-qr-scanner';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import App from './App.tsx';
import { msalConfig } from './const/msal-config.ts';
import { ApiProvider, defaultApiConfig } from './contexts/ApiContext/ApiProvider.tsx';
import { AuthenticationProvider } from './contexts/AuthenticationContext';
import { BandwidthProvider } from './contexts/BandwidthContext';
import { ConfigProvider } from './contexts/ConfigContext/ConfigProvider.tsx';
import { HealthCenterProvider } from './contexts/HealthCenterContext';
import { QueryQueueProvider } from './contexts/QueryQueueContext';
import { UserAppProvider } from './contexts/UserAppContext';
import i18n from './i18n.ts';
import { initFcm } from './notifications/fcm.ts';
import { basePath } from './utils/base-path.ts';

const apiConfig = localStorage.getItem('apiConfig')
  ? JSON.parse(localStorage.getItem('apiConfig') as string)
  : defaultApiConfig;

Api.initialize(apiConfig.id);
Api.enableLocalDatastore();
Api.serverURL = apiConfig.url;
initFcm();

setZXingModuleOverrides({
  locateFile: (path, prefix) => {
    if (path.endsWith('.wasm')) {
      return `${basePath}zxing_reader.wasm`;
    }
    return prefix + path;
  },
});

const queryClient = new QueryClient();
const msalInstance = new PublicClientApplication(msalConfig);

const renderApp = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={mobileTheme}>
            <I18nextProvider i18n={i18n}>
              <MsalProvider instance={msalInstance}>
                <UserAppProvider>
                  <ApiProvider>
                    <HealthCenterProvider>
                      <BandwidthProvider>
                        <AuthenticationProvider>
                          <ConfigProvider>
                            <QueryQueueProvider>
                              <App />
                            </QueryQueueProvider>
                          </ConfigProvider>
                        </AuthenticationProvider>
                      </BandwidthProvider>
                    </HealthCenterProvider>
                  </ApiProvider>
                </UserAppProvider>
              </MsalProvider>
            </I18nextProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </StrictMode>
  );

// MSAL must be initialized before any API call (including the `handleRedirectPromise` used
// by the redirect login flow). We await it before rendering because child effects run before
// MsalProvider's own initialization effect, so the instance would otherwise still be
// uninitialized when AuthenticationProvider reads the redirect result. We still render if
// initialization fails (e.g. storage/cookie restrictions) so non-Microsoft flows (password
// login, already-authenticated users) keep working instead of getting a blank screen.
msalInstance
  .initialize()
  .catch((error) => datadogLogs.logger.error('MSAL initialization failed', { error }))
  .then(renderApp);
